<template>
  <div class="checkin-container">
    <el-card v-loading="loading" element-loading-text="加载中..." class="checkin-card">
      <template #header>
        <div class="card-header">
          <h2>每日签到</h2>
        </div>
      </template>
      
      <div class="checkin-content">
        <!-- 签到状态 -->
        <div class="checkin-status">
          <div class="status-icon" :class="{ 'checked-in': hasCheckedIn }">
            <el-icon v-if="hasCheckedIn" :size="60" color="#67C23A">
              <SuccessFilled />
            </el-icon>
            <el-icon v-else :size="60" color="#909399">
              <CircleCheck />
            </el-icon>
          </div>
          <div class="status-text">
            <h3 v-if="hasCheckedIn">今日已签到</h3>
            <h3 v-else>今日未签到</h3>
            <p v-if="hasCheckedIn">签到时间：{{ checkinTime }}</p>
            <p v-else>点击下方按钮完成签到</p>
          </div>
        </div>
        
        <!-- 签到按钮 -->
        <div class="checkin-action">
          <el-button 
            type="primary" 
            size="large" 
            :disabled="hasCheckedIn || checkinLoading"
            :loading="checkinLoading"
            @click="handleCheckin">
            {{ hasCheckedIn ? '已签到' : '立即签到' }}
          </el-button>
        </div>
        
        <!-- 签到奖励 -->
        <div class="checkin-reward">
          <div class="reward-info">
            <el-icon :size="24" color="#E6A23C">
              <Coin />
            </el-icon>
            <span class="reward-text">签到奖励</span>
            <span class="reward-points">+{{ dailyCheckinPoints }} 积分</span>
          </div>
        </div>
        
        <!-- 连续签到 -->
        <div class="consecutive-checkin">
          <div class="consecutive-info">
            <el-icon :size="20" color="#409EFF">
              <Calendar />
            </el-icon>
            <span class="consecutive-text">连续签到</span>
            <span class="consecutive-days">{{ consecutiveDays }} 天</span>
          </div>
        </div>
      </div>
    </el-card>
    
    <!-- 签到记录 -->
    <el-card class="history-card">
      <template #header>
        <div class="card-header">
          <h2>签到记录</h2>
        </div>
      </template>
      
      <div class="history-content">
        <el-table :data="checkinHistory" v-loading="historyLoading" stripe>
          <el-table-column prop="checkin_date" label="签到日期" width="180" />
          <el-table-column prop="checkin_time" label="签到时间" width="180">
            <template #default="{ row }">
              {{ formatDateTime(row.checkin_time) }}
            </template>
          </el-table-column>
          <el-table-column prop="points" label="获得积分" width="120">
            <template #default="{ row }">
              <span class="points-text">+{{ row.points }}</span>
            </template>
          </el-table-column>
        </el-table>
        
        <!-- 分页 -->
        <div class="pagination-container">
          <el-pagination
            v-model:current-page="currentPage"
            v-model:page-size="pageSize"
            :page-sizes="[10, 20, 50, 100]"
            :total="total"
            layout="total, sizes, prev, pager, next, jumper"
            @size-change="handleSizeChange"
            @current-change="handleCurrentChange"
          />
        </div>
      </div>
    </el-card>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { ElMessage } from 'element-plus';
import { SuccessFilled, CircleCheck, Coin, Calendar } from '@element-plus/icons-vue';
import * as pointApi from '../../api/point';

// 加载状态
const loading = ref(true);
const historyLoading = ref(false);
const checkinLoading = ref(false);

// 签到状态
const hasCheckedIn = ref(false);
const checkinTime = ref('');
const dailyCheckinPoints = ref(0);
const consecutiveDays = ref(0);

// 签到记录
const checkinHistory = ref([]);
const currentPage = ref(1);
const pageSize = ref(20);
const total = ref(0);

// 获取签到状态
async function fetchCheckinStatus() {
  try {
    const response = await pointApi.getCheckinStatus();
    if (response.code === 200) {
      hasCheckedIn.value = response.data.has_checked_in;
      consecutiveDays.value = response.data.consecutive_days;
    }
  } catch (error) {
    ElMessage.error('获取签到状态失败：' + (error.message || '未知错误'));
    console.error('获取签到状态失败:', error);
  }
}

// 获取签到记录
async function fetchCheckinHistory() {
  historyLoading.value = true;
  try {
    const response = await pointApi.getCheckinHistory({
      page: currentPage.value,
      page_size: pageSize.value
    });
    if (response.code === 200) {
      checkinHistory.value = response.data.data || [];
      total.value = response.data.total || 0;
    }
  } catch (error) {
    ElMessage.error('获取签到记录失败：' + (error.message || '未知错误'));
    console.error('获取签到记录失败:', error);
  } finally {
    historyLoading.value = false;
  }
}

// 签到
async function handleCheckin() {
  if (hasCheckedIn.value) {
    ElMessage.warning('今天已经签到过了');
    return;
  }
  
  checkinLoading.value = true;
  try {
    const response = await pointApi.checkin();
    if (response.code === 200) {
      ElMessage.success('签到成功！获得 ' + response.data.points + ' 积分');
      hasCheckedIn.value = true;
      checkinTime.value = formatDateTime(response.data.checkin_time);
      dailyCheckinPoints.value = response.data.points;
      consecutiveDays.value += 1;
      
      // 刷新签到记录
      await fetchCheckinHistory();
    }
  } catch (error) {
    ElMessage.error('签到失败：' + (error.message || '未知错误'));
    console.error('签到失败:', error);
  } finally {
    checkinLoading.value = false;
  }
}

// 格式化日期时间
function formatDateTime(dateTime) {
  if (!dateTime) return '';
  const date = new Date(dateTime);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
}

// 分页大小改变
function handleSizeChange(val) {
  pageSize.value = val;
  currentPage.value = 1;
  fetchCheckinHistory();
}

// 当前页改变
function handleCurrentChange(val) {
  currentPage.value = val;
  fetchCheckinHistory();
}

// 页面加载
onMounted(async () => {
  loading.value = true;
  try {
    await Promise.all([
      fetchCheckinStatus(),
      fetchCheckinHistory()
    ]);
  } finally {
    loading.value = false;
  }
});
</script>

<style scoped>
.checkin-container {
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
}

.checkin-card {
  margin-bottom: 20px;
}

.history-card {
  margin-bottom: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card-header h2 {
  margin: 0;
  font-size: 18px;
  color: #333;
}

.checkin-content {
  padding: 20px 0;
}

.checkin-status {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px 0;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 12px;
  margin-bottom: 30px;
}

.status-icon {
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
}

.status-icon.checked-in {
  background: rgba(103, 194, 58, 0.2);
}

.status-text {
  text-align: center;
  color: #fff;
}

.status-text h3 {
  margin: 0 0 10px 0;
  font-size: 24px;
  font-weight: bold;
}

.status-text p {
  margin: 0;
  font-size: 14px;
  opacity: 0.9;
}

.checkin-action {
  display: flex;
  justify-content: center;
  margin-bottom: 30px;
}

.checkin-action .el-button {
  width: 200px;
  height: 50px;
  font-size: 18px;
  border-radius: 25px;
}

.checkin-reward {
  background: #f0f9ff;
  border: 1px solid #dcdfe6;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
}

.reward-info {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
}

.reward-text {
  font-size: 16px;
  color: #606266;
}

.reward-points {
  font-size: 20px;
  font-weight: bold;
  color: #E6A23C;
}

.consecutive-checkin {
  background: #ecf5ff;
  border: 1px solid #d9ecff;
  border-radius: 8px;
  padding: 15px 20px;
  margin-bottom: 20px;
}

.consecutive-info {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
}

.consecutive-text {
  font-size: 14px;
  color: #606266;
}

.consecutive-days {
  font-size: 20px;
  font-weight: bold;
  color: #409EFF;
}

.history-content {
  padding: 20px 0;
}

.points-text {
  color: #E6A23C;
  font-weight: bold;
}

.pagination-container {
  display: flex;
  justify-content: center;
  margin-top: 20px;
}
</style>
